import React, { useEffect } from "react";
import { Table, Badge, Alert, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import moment from "moment/moment";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { RenegociacaoType } from "../../../../../../../contexts/RenegociacaoContext/types";
import { TicketType } from "../../../../../../../types/TicketType";
import { DommusAlertaAmarelo } from "../../../../../../../components/DommusAlertaAmarelo";
import GuService from "../../../../../../../services/GuService";

interface HistoricoRenegociacoesProps {
  renegociacoes?: RenegociacaoType[] | null;
  ticket: TicketType;
  onPodeCriarChange: (pode: boolean) => void;
}

const resolverAditivoCancelado = (aditivoCancelado: string | number | boolean | null | undefined) => {
  return ['1', 1, true, 'true', 't'].includes(aditivoCancelado as never);
};

// Uma renegociação bloqueia a criação de uma nova apenas enquanto estiver em aberto.
// Canceladas (solicitação 'C' ou aditivo cancelado) e finalizadas não bloqueiam.
const renegociacaoEmAberto = (renegociacao: RenegociacaoType) => {
  if (renegociacao.solicitacao_situacao === 'C' || resolverAditivoCancelado(renegociacao.aditivo_cancelado)) {
    return false;
  }
  if (renegociacao.solicitacao_situacao === 'P') {
    return true;
  }
  return renegociacao.solicitacao_situacao === 'F' && renegociacao.aditivo_situacao !== 'F';
};

export function HistoricoRenegociacoes({ renegociacoes, ticket, onPodeCriarChange }: HistoricoRenegociacoesProps) {
  // A consulta do backend retorna ORDER BY cadastrado_em ASC, então a atual é a última.
  const renegociacaoAtual = renegociacoes && renegociacoes.length ? renegociacoes[renegociacoes.length - 1] : null;
  const emAndamento = !!renegociacaoAtual && renegociacaoEmAberto(renegociacaoAtual);
  const podeCriarRenegociacao = !renegociacoes || renegociacoes.length === 0 || !renegociacoes.some(renegociacaoEmAberto);

  useEffect(() => {
    onPodeCriarChange(podeCriarRenegociacao);
  }, [renegociacoes, podeCriarRenegociacao, onPodeCriarChange]);

  if (!renegociacoes || renegociacoes.length === 0) {
    return (
      <Alert variant="warning" className="mb-0">Nenhum histórico de renegociações.</Alert>
    );
  }

  const badgeFor = (s: string | undefined) => {
    switch (s) {
      case 'P':
        return <Badge variant="warning">Pendente</Badge>;
      case 'F':
        return <Badge variant="success">Finalizada</Badge>;
      case 'C':
        return <Badge variant="danger">Cancelada</Badge>;
      default:
        return <Badge variant="secondary">{s}</Badge>;
    }
  };

  const badgeAditivo = (situacao: string | undefined, aditivoCancelado?: string | number | boolean | null) => {
    if (resolverAditivoCancelado(aditivoCancelado)) {
      return <Badge variant="danger">Cancelado</Badge>;
    }
    switch (situacao) {
      case 'S':
        return <Badge variant="info">Solicitado</Badge>;
      case 'A':
        return <Badge variant="success">Autorizado</Badge>;
      case 'R':
        return <Badge variant="warning">Renegociação</Badge>;
      case 'I':
        return <Badge variant="secondary">Impresso</Badge>;
      case 'F':
        return <Badge variant="dark">Finalizado</Badge>;
      default:
        return <small className="text-muted">{situacao || '-'}</small>;
    }
  };
  
  return (
    <>
      {emAndamento && (
        <button
          className="p-0 border-0 bg-transparent w-100 text-start mb-2"
          onClick={() => {
            const urlVendas = GuService.getLocalState("linkVendas");
            window.open(
              urlVendas + 'index_ui.php?mgr=MQ==&ui=NDc=&id_processo=' + ticket.id_processo,
              '_blank'
            );
          }}
          style={{ cursor: 'pointer' }}
        >
          <DommusAlertaAmarelo mensagem="Renegociação em andamento. Clique para visualizar no Vendas." />
        </button>
      )}
      <Table striped bordered hover size="sm" className="mb-0">
      <thead>
        <tr>
          <th>Sit. solicitação</th>
          <th>Sit. do aditivo</th>
          <th>Autorizado em</th>
          <th>Ações</th>
        </tr>
      </thead>
      <tbody>
        {renegociacoes.map((r: any, idx: number) => (
          <tr key={r.id_solicitacao_renegociacao || idx}>
            <td>{badgeFor(r.solicitacao_situacao)}</td>
            <td>{badgeAditivo(r.aditivo_situacao, r.aditivo_cancelado)}</td>
            <td>
              {r.aditivo_autorizado_em ? (
                <small className="text-muted">{moment(r.aditivo_autorizado_em).format('DD/MM/YYYY')}</small>
              ) : (
                <small className="text-muted">-</small>
              )}
            </td>
            <td>
              <OverlayTrigger overlay={<Tooltip id={`tooltip-aditivo-${r.id_solicitacao_renegociacao}`}>Abrir aditivo no Vendas</Tooltip>}>
                <Button variant="link" size="sm" onClick={() => {
                  const urlVendas = GuService.getLocalState("linkVendas");
                  window.open(
                    urlVendas + 'index_ui.php?mgr=MQ==&ui=NTQ=&id_processo=' + ticket.id_processo + '&tab=desk&idx_aditivo=' + idx,
                    '_blank'
                  );
                }} aria-label={`Abrir aditivo ${r.id_aditivo || ''} no vendas`}>
                  <FontAwesomeIcon icon={faExternalLinkAlt as IconProp} />
                </Button>
              </OverlayTrigger>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
    </>
  );
}

export default HistoricoRenegociacoes;







